import { HosCalculator } from "./hosCalculator";
import { EwdCalculator } from "./ewdCalculator";
import {
    populateViolationReason,
    IValidation,
    getDefaultAvailability,
    getWorkdayMaximumWithDate,
    getDefaultValidateResult,
    HOURS,
    timeSpanToMs,
    offDutyStatuses,
    IState,
    IValidateStateAndResult,
    IValidateStateByRuleset,
    IRuleSetDateRange,
    AUSTRALIARULESETS,
    ICalculationResult,
    parseDateTime,
    compareDatesWithSequenceIds,
    ICustomCodeState,
    DEFAULT_CUSTOM_CODE_STATE
} from "./helpers";
import { getEffectiveRuleSets, getRuleSetForCalculation } from "./rulesets";
import type { IViolation } from "../interfaces/violation";
import type { IDutyStatusLog } from "../interfaces/dutyStatusLog";
import type { IUserHosRuleSet } from "../interfaces/userHosRuleSet";
import type { IHosRuleset, IHosRulesetConfig } from "../interfaces/hosRuleset";
import type { IIdDictionary } from "../interfaces/entity";

const validHosLogStatuses = ["ON", "OFF", "SB", "D", "YM", "PC", "PC_Exempted", "WT"];
const validEwdLogStatuses = ["Work", "Rest", "WorkExemption"];

interface IDstCalculator {
    timeSpanParse: (span: string) => Date;
    toLocalDateTime: (date: Date | string, timeZoneId?: string, accountForDst?: boolean) => Date;
    toReverseOffsetDateTime: (date: Date, timeZoneId?: string, accountForDst?: boolean) => Date;
}

interface IHosEngineInputLog {
    dateTime: string | Date;
    status: string;
    id?: string;
    sequence?: string;
    isIgnored?: boolean;
    deferralStatus?: IDutyStatusLog["deferralStatus"];
    deferralMinutes?: number;
}

interface ILocalizedDutyStatusLog {
    dateTime: Date;
    id?: string;
    status: string;
    deferralStatus?: IDutyStatusLog["deferralStatus"];
    deferralMinutes?: number;
}

interface IValidateHosParams {
    logs: IHosEngineInputLog[];
    ruleSets: IUserHosRuleSet[];
    dummyLogDate?: string;
    customCodes: ICustomCodeState;
    timeZoneId?: string;
    fromDate?: string;
    getAllRecaps?: boolean;
    getDiscreteAvailability?: boolean;
    getNextAvailabilityEvent?: boolean;
    getNextHosEventDate?: boolean;
}

interface IValidateHosWithStateParams extends Omit<IValidateHosParams, "customCodes"> {
    customCodes: Partial<ICustomCodeState>;
    prevState?: IValidateStateByRuleset | string;
}

export class EngineCalculator {
    private readonly timeSpanParse: (span?: string) => number | undefined;

    /**
     * Sorts the distinct rulesets by the cycle restart time, ascending. This is required for cycle availabilities to be calculated correctly when switching to a ruleset with a greater cycle restart time.
     * @param distinctRulesets a dictionary of distinct rulesets.
     * @private
     */
    private getRuleSetValidationOrder(distinctRulesets: { [id: string]: IUserHosRuleSet }, useLegacyTexasShortHaulRulesets: boolean): string[] {
        let distinctRulesetIds = Object.keys(distinctRulesets);
        let configDict: { [id: string]: IHosRulesetConfig | undefined } = {};
        let ruleSets = getEffectiveRuleSets(useLegacyTexasShortHaulRulesets);
        distinctRulesetIds.forEach((id: string) => {
            let ruleset = distinctRulesets[id];
            let rulesetKey = ruleset.hosRuleSet.id.replace("HosRuleSet", "");
            configDict[id] = ruleSets[rulesetKey] ? ruleSets[rulesetKey].config : undefined;
        });
        return distinctRulesetIds.sort((a, b) => {
            let aConfig = configDict[a];
            let bConfig = configDict[b];
            return (aConfig?.cycle?.cycleRestartMinimum ?? 0) - (bConfig?.cycle?.cycleRestartMinimum ?? 0);
        });
    }

    private getRuleSetDateRanges(sets: IUserHosRuleSet[]): IIdDictionary<IRuleSetDateRange[]> {
        let ruleSetsDateRanges: IIdDictionary<IRuleSetDateRange[]> = {};
        for (let i = 0, j = sets.length; i < j; i++) {
            let set = sets[i],
                startDateTime = new Date(<string>set.dateTime),
                nextSet = i < j - 1 ? sets[i + 1] : null,
                nextDateTime = nextSet ? new Date(<string>nextSet.dateTime) : null,
                key = this.getRuleSetKey(set);

            if (key in ruleSetsDateRanges) {
                ruleSetsDateRanges[key].push({ start: startDateTime, end: nextDateTime });
            } else {
                ruleSetsDateRanges[key] = [{ start: startDateTime, end: nextDateTime }];
            }
        }
        return ruleSetsDateRanges;
    }

    private reverseResultDateTimeOffSet(resultsToShift: IValidation, reverseOffSetDateTime: (date: Date) => Date): IValidation {
        // The pre-strict implementation wrote `null` (not `undefined`) into the shifted result when a source date was
        // missing, and the serialized EWD result shape depends on that. The result interfaces declare these fields as
        // `Date | undefined` because every EWD result date is populated in practice (violations, rest infos and
        // availability periods are always built with both dates), so `null` is a legacy compatibility value rather
        // than part of the declared contract. `asResultDate` is the single place that bridges the two. Both halves of
        // that invariant are pinned by tests/engineCalculator.ewdDates.test.ts.
        type TLegacyShiftedDate = Date | null;
        let shiftDate = (date: Date | undefined): TLegacyShiftedDate => (date ? reverseOffSetDateTime(date) : null);
        let asResultDate = (date: TLegacyShiftedDate): Date => date as Date;
        resultsToShift.violations = resultsToShift.violations.map(violation => {
            violation.toDate = asResultDate(shiftDate(violation.toDate));
            violation.fromDate = asResultDate(shiftDate(violation.fromDate));
            return violation;
        });
        if (resultsToShift.availability && resultsToShift.availability.recap) {
            resultsToShift.availability.recap.map(recap => {
                recap.dateTime = reverseOffSetDateTime(recap.dateTime);
                return recap;
            });
        }
        if (resultsToShift.ewdAvailability) {
            resultsToShift.ewdAvailability.map(ewdAvailability => {
                ewdAvailability.startDate = asResultDate(shiftDate(ewdAvailability.startDate));
                ewdAvailability.toDate = asResultDate(shiftDate(ewdAvailability.toDate));
                return ewdAvailability;
            });
        }
        if (resultsToShift.availability && resultsToShift.availability.cycleAvailabilities !== undefined) {
            resultsToShift.availability.cycleAvailabilities.map(cycleAvail => {
                cycleAvail.dateTime = reverseOffSetDateTime(cycleAvail.dateTime);
                return cycleAvail;
            });
        }
        if (resultsToShift.daySummaries) {
            resultsToShift.daySummaries = resultsToShift.daySummaries.map(day => {
                if (day.restInfos) {
                    day.restInfos = day.restInfos.map(restInfo => {
                        restInfo.toDate = asResultDate(shiftDate(restInfo.toDate));
                        restInfo.fromDate = asResultDate(shiftDate(restInfo.fromDate));
                        return restInfo;
                    });
                }
                return day;
            });
        }
        if (resultsToShift.workdaySummaries) {
            resultsToShift.workdaySummaries = resultsToShift.workdaySummaries.map(workday => {
                workday.toDate = asResultDate(shiftDate(workday.toDate));
                workday.fromDate = asResultDate(shiftDate(workday.fromDate));
                return workday;
            });
        }
        if (resultsToShift.cycleSummaries) {
            resultsToShift.cycleSummaries = resultsToShift.cycleSummaries.map(cycle => {
                cycle.dateTime = asResultDate(shiftDate(cycle.dateTime));
                return cycle;
            });
        }
        return resultsToShift;
    }

    private sortFunc(a: IUserHosRuleSet, b: IUserHosRuleSet) {
        return a.dateTime > b.dateTime ? 1 : a.dateTime < b.dateTime ? -1 : 0;
    }

    private simplifyAndLocalizeLog(log: IHosEngineInputLog, dateLocalizer?: (date: Date | string) => Date): ILocalizedDutyStatusLog {
        let simpleLog: ILocalizedDutyStatusLog = {
            dateTime: dateLocalizer ? dateLocalizer(log.dateTime) : new Date(log.dateTime),
            id: log.id,
            status: log.status
        };
        if (simpleLog.status === "ExemptionOffDutyDeferral") {
            simpleLog.deferralStatus = log.deferralStatus;
            simpleLog.deferralMinutes = log.deferralMinutes;
        }
        return simpleLog;
    }

    private getDistinctRuleSets(rulesets: IUserHosRuleSet[]): { [id: string]: IUserHosRuleSet } {
        let distinctRulesets: { [id: string]: IUserHosRuleSet } = {};

        for (let ruleset of rulesets) {
            if (!ruleset.hosRuleSet || !ruleset.hosRuleSet.id) {
                throw new Error("Invalid user ruleset provided: " + JSON.stringify(rulesets));
            }
            var id = this.getRuleSetKey(ruleset);
            if (!distinctRulesets.hasOwnProperty(id)) {
                distinctRulesets[id] = ruleset;
            }
        }
        return distinctRulesets;
    }

    private populateViolationReasons(result: IValidation, rule: IHosRuleset) {
        // TODO: remove Violation.reason from ObjectModel.
        if (rule && result.violations.length > 0) {
            let availabilityHours = getDefaultAvailability(rule);
            let cycleMaximum = availabilityHours.cycle !== undefined ? timeSpanToMs(availabilityHours.cycle) / HOURS : undefined;
            let cycleDrivingMaximum = availabilityHours.cycleDriving !== undefined ? timeSpanToMs(availabilityHours.cycleDriving) / HOURS : undefined;
            let workdayMaximum =
                availabilityHours.workday !== undefined ? timeSpanToMs(availabilityHours.workday) / HOURS : getWorkdayMaximumWithDate(rule);
            let dutyMaximum = availabilityHours.duty !== undefined ? timeSpanToMs(availabilityHours.duty) / HOURS : undefined;
            let drivingMaximum = availabilityHours.driving !== undefined ? timeSpanToMs(availabilityHours.driving) / HOURS : undefined;
            let rest = availabilityHours.rest !== undefined ? timeSpanToMs(availabilityHours.rest) / HOURS : undefined;
            let dutySinceCycleRestMaximum =
                availabilityHours.dutySinceCycleRest !== undefined ? timeSpanToMs(availabilityHours.dutySinceCycleRest) / HOURS : undefined;
            let dailyRest = rule.config.rest && rule.config.rest.dailyRestMinimum ? rule.config.rest.dailyRestMinimum / HOURS : 0;
            let dailyOffMinimum = rule.config.daily && rule.config.daily.offDutyMinimum ? rule.config.daily.offDutyMinimum / HOURS : 0;
            let cycleDays = (rule.config.cycle && rule.config.cycle.cycleDays) || 0;
            let cycleRestMinimum = ((rule.config.cycle && rule.config.cycle.cycleRestMinimum) || 0) / HOURS;
            let cycleRestDays = (rule.config.cycle && rule.config.cycle.cycleRestDays) || 0;
            result.violations.forEach(violation => {
                switch (violation.type) {
                    case "Cycle":
                        violation.daysLimit = cycleDays;
                        violation.hoursLimit = cycleMaximum;
                        break;
                    case "Workday":
                        // WorkdayMaximum calculated we violations are generated. b/c it can be extended with exemptions.
                        // WorkdayMaximum could change base on the date
                        if (!violation.hoursLimit) {
                            violation.hoursLimit = workdayMaximum;
                        }
                        break;
                    case "Duty":
                        violation.hoursLimit = dutyMaximum;
                        break;
                    case "DailyDuty":
                        violation.hoursLimit = dutyMaximum;
                        break;
                    case "Driving":
                        violation.hoursLimit = drivingMaximum;
                        break;
                    case "DailyDriving":
                        violation.hoursLimit = drivingMaximum;
                        break;
                    case "Rest":
                        violation.hoursLimit = rest;
                        break;
                    case "DailyRest":
                        violation.hoursLimit = dailyRest;
                        break;
                    case "DailyOff":
                        violation.hoursLimit = dailyOffMinimum;
                        break;
                    case "DutySinceCycleRest":
                        violation.hoursLimit = dutySinceCycleRestMaximum;
                        break;
                    case "CycleRest":
                        violation.hoursLimit = cycleRestMinimum;
                        violation.daysLimit = cycleRestDays;
                        violation.periodsLimit = rule.config.cycle?.cycleRestPeriods ?? 1;
                        break;
                    case "CycleDriving":
                        violation.daysLimit = cycleDays;
                        violation.hoursLimit = cycleDrivingMaximum;
                        break;
                    case "CycleEnd":
                        violation.daysLimit = cycleDays;
                        break;
                    default:
                        break;
                }
                violation.reason = populateViolationReason(violation);
            });
        }
    }

    private isForEWD(userRulesets: IUserHosRuleSet[]): boolean {
        let lastUserRuleset = userRulesets.length > 0 && userRulesets[userRulesets.length - 1];
        let longLastRulesetId = lastUserRuleset && lastUserRuleset?.hosRuleSet?.id;
        if (typeof longLastRulesetId !== "string") {
            return false;
        }
        let shortLastRulesetId = longLastRulesetId.replace("HosRuleSet", "") || undefined;
        return !!shortLastRulesetId && AUSTRALIARULESETS.indexOf(shortLastRulesetId) !== -1;
    }

    private calculateAvailabilityAndViolationsAndState(
        dummyLogDate: Date | undefined,
        distinctRuleset: IUserHosRuleSet,
        cleanLogs: ILocalizedDutyStatusLog[],
        controlLogs: ILocalizedDutyStatusLog[],
        rulesetDateRanges: IRuleSetDateRange[],
        customCodes: ICustomCodeState,
        prevState?: IState,
        fromDate?: Date,
        driverTimeZone?: string,
        getAllRecaps = false,
        getDiscreteAvailability = false,
        getNextHosEventDateTime = false,
        completedCycleRestartDates: Date[] = [],
        getFullState = false,
        getNextAvailabilityEvent = false
    ): ICalculationResult {
        let rulesetKey = distinctRuleset.hosRuleSet.id.replace("HosRuleSet", "");
        let isEwdRuleset = AUSTRALIARULESETS.indexOf(rulesetKey) !== -1;
        let rulesetObj = getRuleSetForCalculation(rulesetKey, customCodes.disableTexasShortHaulRulesetCorrection);
        if (!rulesetObj) {
            return {
                validation: getDefaultValidateResult(rulesetKey),
                state: undefined
            };
        }
        let result: ICalculationResult;
        let startOfDay: number = this.timeSpanParse(distinctRuleset.startOfDay) || 0;
        let toLocalTime = (date: Date): Date => {
            let localDate = this.dstCalculator.toLocalDateTime(date, driverTimeZone, true);
            return startOfDay ? new Date(localDate.getTime() - startOfDay) : localDate;
        };
        let toUtcTime = (date: Date): Date => {
            let localDate = this.dstCalculator.toReverseOffsetDateTime(date, driverTimeZone, true);
            return startOfDay ? new Date(localDate.getTime() + startOfDay) : localDate;
        };
        if (isEwdRuleset) {
            let ewdResult = EwdCalculator(dummyLogDate, cleanLogs, rulesetObj);
            let reverseOffSetDateTime = (date: Date) => this.dstCalculator.toReverseOffsetDateTime(date, driverTimeZone, true);
            result = { validation: this.reverseResultDateTimeOffSet(ewdResult.validation, reverseOffSetDateTime), state: ewdResult.state };
        } else {
            result = HosCalculator(
                dummyLogDate,
                cleanLogs,
                rulesetObj,
                controlLogs,
                toLocalTime,
                toUtcTime,
                distinctRuleset,
                rulesetDateRanges,
                customCodes,
                prevState,
                fromDate,
                getAllRecaps,
                getDiscreteAvailability,
                getNextHosEventDateTime,
                completedCycleRestartDates,
                getFullState,
                getNextAvailabilityEvent
            );
            this.populateViolationReasons(result.validation, rulesetObj);
        }

        return result;
    }

    private getRuleSetKey(currentRuleSet: IUserHosRuleSet): string {
        if (currentRuleSet.startOfDay) {
            return currentRuleSet.hosRuleSet.id + currentRuleSet.startOfDay;
        }
        return currentRuleSet.hosRuleSet.id + "00:00:00";
    }

    constructor(private readonly dstCalculator: IDstCalculator) {
        this.timeSpanParse = (span?: string) => (span ? dstCalculator.timeSpanParse(span).getTime() : undefined);
    }

    validateHos(params: IValidateHosParams) {
        return this.validate(
            params.logs,
            params.ruleSets,
            params.dummyLogDate,
            params.customCodes,
            params.timeZoneId,
            params.fromDate,
            params.getAllRecaps,
            params.getDiscreteAvailability,
            params.getNextAvailabilityEvent,
            params.getNextHosEventDate
        );
    }

    validateHosWithState(params: IValidateHosWithStateParams) {
        return this.validateWithState(
            params.logs,
            params.ruleSets,
            params.dummyLogDate,
            params.customCodes,
            params.timeZoneId,
            params.prevState,
            params.fromDate
        );
    }

    validate(
        logs: IHosEngineInputLog[],
        userRulesets: IUserHosRuleSet[],
        dummyDate: string | undefined,
        customCodes: ICustomCodeState,
        driverTimeZone?: string,
        fromDate?: string,
        getAllRecaps = false,
        getDiscreteAvailability = false,
        getNextAvailabilityEvent = false,
        getNextHosEventDateTime = false
    ): IValidation {
        return this.validateWithState(
            logs,
            userRulesets,
            dummyDate,
            customCodes,
            driverTimeZone,
            undefined,
            fromDate,
            getAllRecaps,
            getDiscreteAvailability,
            getNextHosEventDateTime,
            false,
            getNextAvailabilityEvent
        ).validation;
    }

    jsonDateParser(_key: string, value: unknown) {
        return parseDateTime(value);
    }

    validateWithState(
        logs: IHosEngineInputLog[],
        userRulesets: IUserHosRuleSet[],
        dummyDate: string | undefined,
        customCodes: Partial<ICustomCodeState>,
        driverTimeZone?: string,
        prevStateByRuleSet?: IValidateStateByRuleset | string,
        fromDate?: string,
        getAllRecaps = false,
        getDiscreteAvailability = false,
        getNextHosEventDateTime = false,
        getFullState = false,
        getNextAvailabilityEvent = false
    ): IValidateStateAndResult {
        if (!driverTimeZone) {
            const debugInfo = {
                driverTimeZone,
                dummyDate,
                fromDate,
                logsCount: logs?.length ?? 0,
                userRulesetsCount: userRulesets?.length ?? 0,
                firstLog: logs?.[0] ? { dateTime: logs[0].dateTime, status: logs[0].status } : null,
                lastLog: logs?.length > 0 ? { dateTime: logs[logs.length - 1].dateTime, status: logs[logs.length - 1].status } : null,
                firstRuleset: userRulesets?.[0] ? { id: userRulesets[0].hosRuleSet?.id, dateTime: userRulesets[0].dateTime } : null
            };
            throw new Error(`Driver's timezone must be provided to EngineCalculator.Validate. Debug: ${JSON.stringify(debugInfo)}`);
        }
        let dateLocalizer = (date: Date | string) => this.dstCalculator.toLocalDateTime(date, driverTimeZone);
        userRulesets.sort(this.sortFunc);
        let dummyLogDate: Date | undefined = undefined,
            fromDateCutoff: Date | undefined = undefined,
            cleanLogs: ILocalizedDutyStatusLog[] = [],
            controlLogs: ILocalizedDutyStatusLog[] = [],
            cleanRulesets: IUserHosRuleSet[] = [],
            defaultResults = getDefaultValidateResult("None"),
            previousStateByRuleSet = prevStateByRuleSet;

        logs.sort(compareDatesWithSequenceIds);

        if (dummyDate) {
            dummyLogDate = new Date(dummyDate);
        }
        if (fromDate) {
            fromDateCutoff = new Date(fromDate);
        }
        if (typeof previousStateByRuleSet === "string") {
            previousStateByRuleSet = JSON.parse(previousStateByRuleSet, this.jsonDateParser);
        }
        if (this.isForEWD(userRulesets)) {
            if (dummyDate) {
                dummyLogDate = dateLocalizer(dummyDate);
            }
            this.filterAndLocalizeEwdLogs(logs, dummyLogDate, cleanLogs, userRulesets, cleanRulesets, dateLocalizer);
        } else {
            this.filterAndLocalizeHosLogs(logs, dummyLogDate, cleanLogs, controlLogs, userRulesets, cleanRulesets);
        }
        let distinctRulesets = this.getDistinctRuleSets(cleanRulesets);
        const finalCustomCodes = { ...DEFAULT_CUSTOM_CODE_STATE, ...customCodes };
        let ruleSetValidationOrder = finalCustomCodes.disableCycleRestartUsedInFollowingValidations
            ? Object.keys(distinctRulesets)
            : this.getRuleSetValidationOrder(distinctRulesets, finalCustomCodes.disableTexasShortHaulRulesetCorrection);
        let completedCycleRestartDates: Date[] = [];
        let mostRecentRuleSetResult = defaultResults,
            currentRuleSet = cleanRulesets.length ? cleanRulesets[cleanRulesets.length - 1] : null,
            currentRuleSetId = currentRuleSet && this.getRuleSetKey(currentRuleSet),
            validViolations: IViolation[] = [],
            nonSplitValidViolations: IViolation[] = [],
            ruleSetsDateRanges = this.getRuleSetDateRanges(cleanRulesets),
            rulesetsThatCannotChange: IUserHosRuleSet[] = [],
            newState: { [id: string]: IState | undefined } = {};

        ruleSetValidationOrder.forEach(distinctRulesetId => {
            if (!distinctRulesets.hasOwnProperty(distinctRulesetId)) {
                return;
            }
            let distinctRuleset = distinctRulesets[distinctRulesetId];
            let prevState: IState | undefined =
                previousStateByRuleSet && typeof previousStateByRuleSet !== "string"
                    ? previousStateByRuleSet[distinctRulesetId]
                    : undefined;
            let rulesetDateRanges = ruleSetsDateRanges[distinctRulesetId];
            let result = this.calculateAvailabilityAndViolationsAndState(
                dummyLogDate,
                distinctRuleset,
                cleanLogs,
                controlLogs,
                rulesetDateRanges,
                finalCustomCodes,
                prevState,
                fromDateCutoff,
                driverTimeZone,
                getAllRecaps,
                getDiscreteAvailability,
                getNextHosEventDateTime,
                completedCycleRestartDates,
                getFullState,
                getNextAvailabilityEvent
            );
            newState[distinctRulesetId] = result.state;
            if (!finalCustomCodes.disableCycleRestartUsedInFollowingValidations) {
                result.state?.cycles.forEach(cycle => {
                    if (cycle.completedRestartDate !== undefined) {
                        completedCycleRestartDates.push(cycle.completedRestartDate);
                    }
                });
            }
            if (result.validation.violations.length > 0) {
                validViolations = validViolations.concat(result.validation.violations);
            }
            const rulesetNonSplitViolations = result.validation.nonSplitViolations ?? result.validation.violations;
            if (rulesetNonSplitViolations.length > 0) {
                nonSplitValidViolations = nonSplitValidViolations.concat(rulesetNonSplitViolations);
            }
            if (result.validation.rulesetsThatCannotChange && result.validation.rulesetsThatCannotChange.length > 0) {
                rulesetsThatCannotChange = rulesetsThatCannotChange.concat(result.validation.rulesetsThatCannotChange);
            }
            if (currentRuleSet !== null && distinctRulesetId === currentRuleSetId) {
                mostRecentRuleSetResult = result.validation;
            }
        });

        if (rulesetsThatCannotChange.length !== 0) {
            mostRecentRuleSetResult.rulesetsThatCannotChange = rulesetsThatCannotChange;
        }

        if (fromDate && mostRecentRuleSetResult.daySummaries) {
            mostRecentRuleSetResult.daySummaries = mostRecentRuleSetResult.daySummaries.filter(
                daySummary => dateLocalizer(fromDate).toISOString() <= daySummary.dateTime.toISOString()
            );
        }

        mostRecentRuleSetResult.violations = validViolations;
        if (mostRecentRuleSetResult.nonSplitViolations !== undefined) {
            mostRecentRuleSetResult.nonSplitViolations = nonSplitValidViolations;
        }
        return { validation: mostRecentRuleSetResult, stateByRuleset: newState };
    }

    isOffDutyStatus(status: string | null | undefined) {
        return offDutyStatuses.some(candidate => candidate === status);
    }

    filterAndLocalizeHosLogs(
        logs: IHosEngineInputLog[],
        dummyLogDate: Date | undefined,
        cleanLogs: ILocalizedDutyStatusLog[],
        controlLogs: ILocalizedDutyStatusLog[],
        rulesets: IUserHosRuleSet[],
        cleanRulesets: IUserHosRuleSet[]
    ) {
        // filter logs and localize dateTimes
        for (let dutyStatusLog of logs) {
            if (dutyStatusLog.isIgnored === true) {
                continue;
            }
            let localizedLog = this.simplifyAndLocalizeLog(dutyStatusLog);
            if (dummyLogDate && localizedLog.dateTime > dummyLogDate) {
                // Remove logs that are after the requested dummylogdate (toDate)
                continue;
            }
            if (validHosLogStatuses.indexOf(localizedLog.status) > -1) {
                cleanLogs.push(localizedLog);
            } else {
                controlLogs.push(localizedLog);
            }
        }

        let ruleset;
        for (ruleset of rulesets) {
            if (dummyLogDate && new Date(ruleset.dateTime) > dummyLogDate) {
                // Remove rulesets that are after the requested dummylogdate (toDate)
                continue;
            }

            cleanRulesets.push(ruleset);
        }
    }

    // TODO: There's bug inside HOSCalculator, if we ignore duplicated log for HOS, the assert on state object will fail
    filterAndLocalizeEwdLogs(
        logs: IHosEngineInputLog[],
        dummyLogDate: Date | undefined,
        cleanLogs: ILocalizedDutyStatusLog[],
        rulesets: IUserHosRuleSet[],
        cleanRulesets: IUserHosRuleSet[],
        dateLocalizer: (date: Date | string) => Date
    ) {
        // filter logs and localize dateTimes
        let lastLogStatus;
        for (let dutyStatusLog of logs) {
            if (dutyStatusLog.isIgnored === true) {
                continue;
            }
            let currentStatus = dutyStatusLog.status;
            let localizedLog = this.simplifyAndLocalizeLog(dutyStatusLog, dateLocalizer);
            if (dummyLogDate && localizedLog.dateTime > dummyLogDate) {
                // Remove logs that are after the requested dummylogdate (toDate)
                continue;
            }
            // ignore duplicated log
            if ((lastLogStatus === undefined || lastLogStatus !== localizedLog.status) && validEwdLogStatuses.indexOf(currentStatus) > -1) {
                cleanLogs.push(localizedLog);
                lastLogStatus = currentStatus;
            }
        }

        let ruleset;
        for (ruleset of rulesets) {
            let rulesetDate = dateLocalizer(ruleset.dateTime);
            if (dummyLogDate && rulesetDate > dummyLogDate) {
                // Remove rulesets that are after the requested dummylogdate (toDate)
                continue;
            }
            cleanRulesets.push({
                dateTime: rulesetDate,
                hosRuleSet: ruleset.hosRuleSet,
                startOfDay: ruleset.startOfDay
            });
        }
    }
}
